/**
 * TRA-1361: bring a user back to the page they opened before sign-in.
 *
 * Keycloak always redirects to the app origin (`redirect_uri`), so the page
 * the user asked for travels in the OIDC `state` instead. oidc-client-ts keeps
 * that state in local storage and hands it back on the signed-in user, so no
 * new Keycloak redirect URIs are needed.
 */

export interface AuthReturnState {
  returnTo: string
}

// Parameters Keycloak adds on the way back from sign-in. They belong to one
// sign-in attempt only, so they are never carried to the next one.
const OIDC_CALLBACK_PARAMS = ['code', 'state', 'session_state', 'iss', 'error', 'error_description', 'error_uri']

/**
 * The page to come back to after sign-in: path, query string and hash of the
 * current location, minus any leftover sign-in callback parameters.
 */
export function currentReturnPath(location: Pick<Location, 'pathname' | 'search' | 'hash'>): string {
  const params = new URLSearchParams(location.search)
  for (const name of OIDC_CALLBACK_PARAMS) {
    params.delete(name)
  }
  const query = params.toString()
  return `${location.pathname}${query ? `?${query}` : ''}${location.hash}`
}

/**
 * Read the return path from the state that came back with the signed-in user.
 * Only a path on this same origin is accepted; anything else (no state, a
 * full URL, "//other.host", "/\\other.host") gives `fallback`.
 */
export function returnPathFromState(state: unknown, origin: string, fallback: string): string {
  if (typeof state !== 'object' || state === null || !('returnTo' in state)) return fallback
  const { returnTo } = state
  if (typeof returnTo !== 'string' || !returnTo.startsWith('/')) return fallback

  let url: URL
  try {
    url = new URL(returnTo, origin)
  } catch {
    return fallback
  }
  if (url.origin !== origin) return fallback
  return `${url.pathname}${url.search}${url.hash}`
}
